import React, { useMemo } from 'react';
import { Filter, User, Package, Activity } from 'lucide-react';
import { useGetUserProfilesQuery } from '../../store/slices/apiSlice';
import { buildDeliveryAssignOptions } from '../../utils/palletJobFields';

interface CalendarFiltersProps {
  selectedDeliveryUser: string;
  onDeliveryUserChange: (userId: string) => void;
  selectedFulfillmentType: string;
  onFulfillmentTypeChange: (type: string) => void;
  // TEMPORARY: For visual testing
  selectedStatus?: string;
  onStatusChange?: (status: string) => void;
}

export const CalendarFilters: React.FC<CalendarFiltersProps> = ({
  selectedDeliveryUser,
  onDeliveryUserChange,
  selectedFulfillmentType,
  onFulfillmentTypeChange,
  selectedStatus = 'all',
  onStatusChange,
}) => {
  const { data: userProfiles, isLoading } = useGetUserProfilesQuery({
    page: 1,
    page_size: 100,
    ordering: 'user__username',
  });

  const engineerOptions = useMemo(
    () => buildDeliveryAssignOptions(userProfiles),
    [userProfiles]
  );

  const selectedUserName = useMemo(() => {
    if (selectedDeliveryUser === 'all') return 'All Engineers';
    const user = engineerOptions.find((u) => u.id === selectedDeliveryUser);
    return user ? user.label : 'All Engineers';
  }, [selectedDeliveryUser, engineerOptions]);

  const fulfillmentTypeName = useMemo(() => {
    if (selectedFulfillmentType === 'all') return 'All';
    if (selectedFulfillmentType === 'delivery') return 'Delivery';
    if (selectedFulfillmentType === 'collection') return 'Collection';
    if (selectedFulfillmentType === 'pallet') return 'Pallet';
    return 'All';
  }, [selectedFulfillmentType]);

  const statusDisplayName = useMemo(() => {
    if (selectedStatus === 'all') return 'All';
    if (selectedStatus === 'IN') return 'In Production';
    if (selectedStatus === 'PO') return 'Problem Orders';
    if (selectedStatus === 'COM') return 'Completed';
    if (selectedStatus === 'DEL') return 'Delivered';
    if (selectedStatus === 'URG') return 'Urgent';
    return 'All';
  }, [selectedStatus]);

  const displayText = useMemo(() => {
    const parts = [];
    parts.push(selectedUserName);
    if (selectedFulfillmentType !== 'all') {
      parts.push(fulfillmentTypeName);
    }
    if (selectedStatus !== 'all') {
      parts.push(statusDisplayName);
    }
    return parts.join(' • ');
  }, [selectedUserName, selectedFulfillmentType, fulfillmentTypeName, selectedStatus, statusDisplayName]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-gray-700">
          <Filter className="w-5 h-5" />
          <span className="font-medium">Filter by:</span>
        </div>

        <div className="flex items-center gap-2 flex-1">
          <User className="w-4 h-4 text-gray-500" />
          <label htmlFor="delivery-user-filter" className="text-sm font-medium text-gray-600">
            Engineer:
          </label>
          <select
            id="delivery-user-filter"
            value={selectedDeliveryUser}
            onChange={(e) => onDeliveryUserChange(e.target.value)}
            disabled={isLoading}
            className="form-input text-sm flex-1 max-w-xs bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md"
          >
            <option value="all">All Engineers</option>
            {engineerOptions.map((user) => (
              <option key={user.id} value={user.id}>
                {user.label} • [{user.role}]
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 flex-1">
          <Package className="w-4 h-4 text-gray-500" />
          <label htmlFor="fulfillment-type-filter" className="text-sm font-medium text-gray-600">
            Fulfillment:
          </label>
          <select
            id="fulfillment-type-filter"
            value={selectedFulfillmentType}
            onChange={(e) => onFulfillmentTypeChange(e.target.value)}
            className="form-input text-sm flex-1 max-w-xs bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md"
          >
            <option value="all">All</option>
            <option value="delivery">Delivery</option>
            <option value="collection">Collection</option>
            <option value="pallet">Pallet</option>
          </select>
        </div>

        {onStatusChange && (
          <div className="flex items-center gap-2 flex-1">
            <Activity className="w-4 h-4 text-gray-500" />
            <label htmlFor="status-filter" className="text-sm font-medium text-gray-600">
              Status:
            </label>
            <select
              id="status-filter"
              value={selectedStatus}
              onChange={(e) => onStatusChange(e.target.value)}
              className="form-input text-sm flex-1 max-w-xs bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-md"
            >
              <option value="all">All</option>
              <option value="IN">IN - In Production</option>
              <option value="PO">PO - Problem Orders</option>
              <option value="COM">COM - Production complete</option>
              <option value="DEL">DEL - Delivered</option>
              <option value="INC">INC - Incomplete</option>
              <option value="COL">COL - Collected</option>
              <option value="URG">URG - Urgent</option>
            </select>
          </div>
        )}

        <div className="text-sm text-gray-600">
          Showing: <span className="font-semibold text-blue-600">{displayText}</span>
        </div>
      </div>

      {!isLoading && engineerOptions.length === 0 && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-800">
            No users with Delivery or Collection role found. Assign one of those roles to enable
            engineer filtering.
          </p>
        </div>
      )}
    </div>
  );
};
